import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { AUTH_OBJECT_TYPES, useAuth } from '@/hooks/useAuth';
import { appendEscapeParam, encodePathSegment, fetchHistory } from '@/services/utils';

import ItemHistoryAttributes from './ItemHistoryAttributes';
import ItemHistoryEvents from './ItemHistoryEvents';
import ItemHistoryTimeline from './ItemHistoryTimeline';

const TIMELINE_TAB = 'Timeline';
const ATTRIBUTES_TAB = 'Attribute Changes';
const EVENTS_TAB = 'Events';

const ItemHistoryTab = ({ primaryKey }: { primaryKey: string }) => {
    const { auth } = useAuth();

    const canViewAttributes = auth
        ? !auth.isDenied(AUTH_OBJECT_TYPES.OBJECT_TYPE_UI, 'view.attributeHistory')
        : false;
    const canViewEvents = auth
        ? !auth.isDenied(AUTH_OBJECT_TYPES.OBJECT_TYPE_UI, 'view.eventHistory')
        : false;
    // The timeline merges attribute changes and events, so it needs both rights.
    const canViewTimeline = canViewAttributes && canViewEvents;

    const visibleTabs = useMemo(() => {
        const result: string[] = [];
        if (canViewTimeline) result.push(TIMELINE_TAB);
        if (canViewAttributes) result.push(ATTRIBUTES_TAB);
        if (canViewEvents) result.push(EVENTS_TAB);
        return result;
    }, [canViewTimeline, canViewAttributes, canViewEvents]);

    const [activeTab, setActiveTab] = useState<string | undefined>(undefined);

    useEffect(() => {
        setActiveTab((current) =>
            current && visibleTabs.includes(current) ? current : visibleTabs[0]
        );
    }, [visibleTabs]);

    const isAttributesTab = activeTab === ATTRIBUTES_TAB;
    const isTimelineTab = activeTab === TIMELINE_TAB;

    const esc = '~';

    const eventQuery = useQuery({
        queryKey: ['itemEventHistory', primaryKey],
        queryFn: () => fetchHistory(
            appendEscapeParam(`items/${encodePathSegment(primaryKey, esc)}/eventHistory`, esc, primaryKey)
        ),
        enabled: !!primaryKey && canViewEvents,
    });

    const attributeQuery = useQuery({
        queryKey: ['itemAttributeHistory', primaryKey],
        queryFn: () => fetchHistory(
            appendEscapeParam(`items/${encodePathSegment(primaryKey, esc)}/history`, esc, primaryKey)
        ),
        enabled: !!primaryKey && canViewAttributes && (isAttributesTab || isTimelineTab),
    });

    const { data: eventData, isLoading: isLoadingEvents, error: errorEvents } = eventQuery;
    const { data: attributeData, isLoading: isLoadingAttributes, error: errorAttributes } = attributeQuery;

    const handleTabClick = (tabTitle: string) => {
        setActiveTab(tabTitle);
    };

    const renderActiveTab = () => {
        if (isLoadingEvents || isLoadingAttributes) return <p>Loading...</p>;
        if (errorEvents || errorAttributes) return <p>Error loading data</p>;

        if (activeTab === EVENTS_TAB && eventData) {
            return <ItemHistoryEvents data={eventData} />;
        } else if (activeTab === ATTRIBUTES_TAB && attributeData) {
            return <ItemHistoryAttributes data={attributeData} />;
        } else if (activeTab === TIMELINE_TAB && eventData) {
            return <ItemHistoryTimeline data={{ eventData, attributeData }} />;
        }
        return null;
    };

    if (visibleTabs.length === 0) {
        return null;
    }

    return (
        <div className="tw-flex tw-flex-col tw-h-full">
            <ul id="history-tabs" className="nav tab-menu nav-tabs">
                {visibleTabs.map((tab) => (
                    <li
                        key={tab}
                        className={activeTab === tab ? 'active' : ''}
                    >
                        <a onClick={() => handleTabClick(tab)}>{tab}</a>
                    </li>
                ))}
            </ul>

            <div id="history-tabs" className="tab-content tw-flex tw-flex-col tw-flex-1 tw-min-h-0">
                <div className="current-tab tw-flex tw-flex-col tw-flex-1 tw-min-h-0">
                    {renderActiveTab()}
                </div>
            </div>
        </div>
    );
};

export { ItemHistoryTab };
